import { FormEvent, useEffect, useRef, useState, KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { Search } from './icons';
import { SearchSuggestDropdown } from './SearchSuggestDropdown';
import { AdvancedSearchHint } from './AdvancedSearchHint';
import { usePopIn } from '../hooks/usePopIn';
import { searchApi, type SuggestResponse } from '../services/search';

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

interface Props {
  variant: 'desktop' | 'mobile';
  scrolled?: boolean;
  onAfterSubmit?: () => void;
  /** 隐藏"进入高级搜索"提示（移动端 header 弹出搜索框用，高级入口改放展开菜单） */
  hideAdvancedHint?: boolean;
  /** 挂载时自动聚焦输入框（移动端点击搜索图标弹出时用） */
  autoFocus?: boolean;
}

export function HeaderSearch({
  variant,
  scrolled,
  onAfterSubmit,
  hideAdvancedHint,
  autoFocus,
}: Props) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const debouncedQ = useDebouncedValue(q.trim(), 180);
  const enableQuery = debouncedQ.length >= 2;

  const { data, isFetching, error } = useQuery({
    queryKey: ['search', 'suggest', debouncedQ],
    queryFn: ({ signal }) => searchApi.suggest(debouncedQ, signal).then((r) => r.data),
    enabled: enableQuery,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: (failureCount, err) => {
      if (isAxiosError(err) && err.response?.status === 503) return failureCount < 3;
      return false;
    },
    retryDelay: 2000,
  });

  useEffect(() => {
    if (variant !== 'desktop') return;
    function onKeyDown(e: globalThis.KeyboardEvent) {
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) return;
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [variant]);

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      const root = inputRef.current?.closest('[data-search-root]');
      if (!root) return;
      if (!root.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  function flatList(d: SuggestResponse | null | undefined) {
    if (!d) return [] as Array<{ to: string }>;
    return d.terms.map((t) => ({ to: `/search?q=${encodeURIComponent(t.text)}` }));
  }

  function resetAfterNav() {
    setQ('');
    setOpen(false);
    setActiveIdx(-1);
    inputRef.current?.blur();
    onAfterSubmit?.();
  }

  function onKey(e: KeyboardEvent<HTMLInputElement>) {
    const list = flatList(data ?? null);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setActiveIdx((i) => Math.min(i + 1, list.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Escape') {
      setOpen(false);
      setActiveIdx(-1);
      inputRef.current?.blur();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (activeIdx >= 0 && activeIdx < list.length) {
        const target = list[activeIdx];
        navigate(target.to);
      } else {
        const term = q.trim();
        if (term.length >= 2) navigate(`/search?q=${encodeURIComponent(term)}`);
      }
      resetAfterNav();
    }
  }

  function onSubmitForm(e: FormEvent) {
    e.preventDefault();
    const term = q.trim();
    if (term.length >= 2) navigate(`/search?q=${encodeURIComponent(term)}`);
    resetAfterNav();
  }

  let errorState: 'none' | 'building' | 'rate-limited' | 'network' = 'none';
  if (isAxiosError(error)) {
    const status = error.response?.status;
    if (status === 503) errorState = 'building';
    else if (status === 429) errorState = 'rate-limited';
    else if (status) errorState = 'network';
  }

  const showDropdown = open && q.trim().length >= 2;
  const advancedHref =
    q.trim().length >= 2 ? `/search?q=${encodeURIComponent(q.trim())}` : '/search';

  // 搜索建议下拉弹入：从输入框下方轻微展开(顶端为锚)；reduce-motion 下不动。
  usePopIn(dropRef, showDropdown, { origin: 'top center', y: 6, scale: 0.985, duration: 0.16 });

  if (variant === 'mobile') {
    return (
      <div data-search-root className="relative">
        <div className="flex flex-col gap-1">
          <form onSubmit={onSubmitForm} className="group/search">
            <div className="flex items-stretch border-[1.5px] border-line bg-surface h-10 transition-colors hover:border-line-strong focus-within:border-navy">
              <div className="flex items-center pl-2.5 pr-1.5">
                <Search className="h-4 w-4 flex-shrink-0 text-navy" />
              </div>
              <input
                ref={inputRef}
                type="search"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setActiveIdx(-1);
                  setOpen(true);
                }}
                onFocus={() => setOpen(true)}
                onKeyDown={onKey}
                placeholder="搜索数据集 / 字段"
                className="min-w-0 flex-1 border-0 bg-transparent pr-2.5 text-[14px] tracking-[0.02em] text-fg outline-none placeholder:tracking-[0.04em] placeholder:text-fg-faint"
              />
            </div>
          </form>
          {!hideAdvancedHint && (
            <AdvancedSearchHint to={advancedHref} onClick={resetAfterNav} variant="standalone" />
          )}
        </div>
        {showDropdown && (
          <div ref={dropRef} className="absolute left-0 right-0 top-full z-40 mt-1">
            <SearchSuggestDropdown
              data={data ?? null}
              loading={isFetching}
              errorState={errorState}
              query={q.trim()}
              activeIdx={activeIdx}
              onHover={setActiveIdx}
              onAfterClick={resetAfterNav}
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      data-search-root
      className="relative flex h-full w-full items-stretch"
    >
      <form onSubmit={onSubmitForm} className="group/search flex w-full items-stretch">
        <div className="flex items-center pl-4 pr-1.5 text-navy">
          <Search className="h-4 w-4 flex-shrink-0" />
        </div>
        <input
          ref={inputRef}
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setActiveIdx(-1);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKey}
          placeholder="搜索数据集 / 字段"
          className="min-w-0 flex-1 border-0 bg-transparent pr-2 text-[13px] tracking-[0.02em] text-fg outline-none placeholder:tracking-[0.04em] placeholder:text-fg-faint"
        />
        <button
          type="submit"
          className="flex items-center self-stretch rounded-r-full border-l border-line px-3.5 text-[10px] uppercase tracking-[0.22em] text-navy transition-colors hover:bg-surface-muted"
        >
          ↵ 搜索
        </button>
      </form>
      {showDropdown && (
        <div
          ref={dropRef}
          className="absolute right-0 top-full z-40 mt-2 w-[360px] max-w-[calc(100vw-32px)]"
        >
          <SearchSuggestDropdown
            data={data ?? null}
            loading={isFetching}
            errorState={errorState}
            query={q.trim()}
            activeIdx={activeIdx}
            onHover={setActiveIdx}
            onAfterClick={resetAfterNav}
          />
        </div>
      )}
    </div>
  );
}
